"use client";

import Image from "next/image";
import {ReactNode, TransitionEvent, useRef, useState} from "react";
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle";
import AppButton from "@/app/components/atomic/molecules/AppButton";
import GoodToKnow from "@/public/images/homepage/Img/GoodToKnow.png";
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon";
import InlineLink from "@/app/components/homepage/components/InlineLink";

function CollapsibleBlock({id, title, children}: { id: string; title: string; children: ReactNode }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(true);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    const textRef = useRef<HTMLParagraphElement>(null);
    const collapsedHeightRef = useRef(0);
    const panelId = `collapsible-panel-${id}`;
    const buttonId = `collapsible-btn-${id}`;

    function toggle() {
        const text = textRef.current;
        if (!text) return;

        if (isExpanded) {
            setMaxHeight(text.scrollHeight);
            // Double rAF: the start height must be painted once, or the browser skips the transition
            requestAnimationFrame(() => requestAnimationFrame(() => setMaxHeight(collapsedHeightRef.current)));
        } else {
            collapsedHeightRef.current = text.clientHeight;
            setMaxHeight(text.clientHeight);
            setIsClamped(false);
            requestAnimationFrame(() => setMaxHeight(text.scrollHeight));
        }

        setIsExpanded(!isExpanded);
    }

    function handleTransitionEnd(event: TransitionEvent<HTMLParagraphElement>) {
        if (event.target !== event.currentTarget || event.propertyName !== "max-height") return;

        setMaxHeight(undefined);
        if (!isExpanded) setIsClamped(true);
    }

    return (
        <div className="flex flex-col gap-3">
            <SectionTitle level="h3" theme="pastel-blue">
                {title}
            </SectionTitle>

            <p
                ref={textRef}
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                onTransitionEnd={handleTransitionEnd}
                style={{maxHeight}}
                className={`m-0 overflow-hidden text-sm text-pale transition-[max-height] duration-300 ease-in-out ${isClamped ? "line-clamp-2" : ""}`}
            >
                {children}
            </p>

            <button
                id={buttonId}
                onClick={toggle}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                className="flex w-fit items-center gap-2 text-sm text-rm-blue-deep cursor-pointer"
            >
                {isExpanded ? "Réduire" : "Développer"}
                <ChevronIcon open={isExpanded} />
            </button>
        </div>
    );
}

export default function AboutSection() {
    return (
        <section className="w-full bg-white px-2 py-12 md:px-6 md:py-16" data-test="about-section">
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8">
                <div className="flex flex-col gap-2">
                    <span className="w-fit rounded-full bg-pastel-blue px-3.5 py-1.5 text-sm text-pale">
                        Bon à savoir
                    </span>

                    <SectionTitle
                        level="h2"
                        theme="neutral"
                        className="md:[&_h2]:text-4xl md:[&_h2]:leading-[2.75rem]"
                    >
                        Vous bénéficiez toujours de garanties équivalentes ou supérieures à celles exigées par la banque.
                    </SectionTitle>
                </div>

                {/* items-start keeps the image anchored while the collapsible column grows */}
                <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-12">
                    <div className="relative hidden aspect-square w-full overflow-hidden rounded-2xl lg:block lg:max-w-[520px] lg:shrink-0 lg:rounded-tr-[50px]">
                        <Image
                            src={GoodToKnow}
                            alt="Le comparateur d'assurance le mieux noté"
                            fill
                            sizes="(min-width: 1024px) 520px, 100vw"
                            className="object-cover"
                        />
                    </div>

                    <div className="flex flex-col gap-8">
                        <CollapsibleBlock id="about-experts" title="Sans frais cachés, des experts 100 % à vos côtés">
                            Réassurez-moi, c'est avant tout une histoire de famille. L'insurtech a été fondée en 2013 par un médecin et son fils, qui s'étaient lancé le défi de rendre l'assurance accessible, transparente et surtout adaptée aux besoins réels de chacun. Depuis plus de 10 ans, on met notre expertise au service des clients, pour leur permettre de comparer facilement toutes les options sur le marché et trouver la meilleure solution. On vous accompagne à chaque étape, avec des conseils honnêtes et impartiaux. Chez Réassurez-moi, nos experts ne sont pas rémunérés à la commission, leur seul objectif demeure votre satisfaction. Notre accompagnement est sans frais de dossier, et nos éventuels frais de courtage vous sont toujours annoncés en toute transparence, avant toute souscription. C'est notamment le cas pour l'assurance de prêt immobilier, où notre rôle de courtier vous permet de <InlineLink href={"/guide/assurance-pret-immobilier/taux"}> comparer les taux d'assurance de prêt</InlineLink> et de <InlineLink href={"/guide/assurance-pret-immobilier/calcul"}>calculer le coût de votre assurance emprunteur</InlineLink>. Vous êtes ainsi certain de faire le bon choix, en toute transparence et sans frais cachés. Réassurez-moi met également la transparence au cœur de son accompagnement. Nos experts travaillent uniquement pour vous offrir le produit le plus avantageux, au meilleur tarif. Ils restent à vos côtés pour vous guider de A à Z.
                        </CollapsibleBlock>

                        <CollapsibleBlock id="about-comparateur" title="Comment fonctionne notre comparateur d'assurances ?">
                            En quelques clics, vous renseignez votre profil et vos besoins pour filtrer les résultats. L'outil vous propose alors une sélection d'offres personnalisées, clairement classées avec tarifs et garanties, accompagnées de l'avis d'experts pour vous aider à faire un choix éclairé, en toute transparence. Avec notre <InlineLink href="/comparateur-mutuelle-sante">comparateur de mutuelle santé</InlineLink> par exemple, vous visualisez d'un coup d'œil les garanties et les tarifs adaptés à votre situation.
                        </CollapsibleBlock>

                        <CollapsibleBlock id="about-devis" title="Obtenez un devis personnalisé et souscrivez en ligne gratuitement">
                            Après la comparaison, vous utilisez des simulateurs rapides et gratuits pour obtenir en quelques minutes plusieurs devis personnalisés. Vous pouvez par exemple <InlineLink href={"/guide/pret-immobilier/simulation"}>simuler votre assurance de prêt immobilier</InlineLink>, réaliser une <InlineLink href={"/guide/pret-immobilier"}>simulation de prêt immobilier</InlineLink> ou vérifier les <InlineLink href={"/taux-pret-immobilier"}>taux de prêt immobilier</InlineLink> du moment. Pour votre assurance emprunteur, vous comparez facilement tarifs et garanties afin de choisir le meilleur équilibre prix/protection, puis un expert dédié vous accompagne de A à Z, de la souscription au changement de contrat, et reste à vos côtés même après signature.
                        </CollapsibleBlock>

                        <div className="w-fit">
                            <AppButton href="#comparators" smoothScroll>
                                Je compare gratuitement
                            </AppButton>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
